import React, { useState } from 'react';
import { Card, Button, Form, Input, Select, InputNumber, DatePicker, Table, Modal, Space, Tag, message, Tooltip, Alert, Result } from 'antd';
import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import storageService from '../services/storageService';
import { formatCurrency, formatConsumption } from '../utils/formatters';
import InvoiceDetail from '../components/Invoices/InvoiceDetail';
import dayjs from 'dayjs';

const BillingPage = () => {
  const { invoices, subscribers, meters, readings, addInvoice, updateInvoice, updateReadingInvoiceLink } = useApp();
  const { user } = useAuth();
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [billingPeriodAuto, setBillingPeriodAuto] = useState('');
  const [periodDates, setPeriodDates] = useState({ startDate: null, endDate: null });
  const isViewer = user?.role === 'viewer';

  // Calculate billing period automatically when subscriber changes
  const handleSubscriberChange = (subscriberId) => {
    if (!subscriberId) {
      setBillingPeriodAuto('');
      setPeriodDates({ startDate: null, endDate: null });
      return;
    }

    // Get subscriber's meters
    const subscriberMeters = meters.filter(m => m.subscriberId === subscriberId);
    if (subscriberMeters.length === 0) {
      setBillingPeriodAuto('');
      setPeriodDates({ startDate: null, endDate: null });
      return;
    }

    // Get all readings for subscriber's meters
    const meterIds = subscriberMeters.map(m => m.id);
    const metersReadings = readings.filter(r => meterIds.includes(r.meterId));

    if (metersReadings.length < 2) {
      setBillingPeriodAuto('');
      setPeriodDates({ startDate: null, endDate: null });
      return;
    }

    // Sort readings by date
    const sortedReadings = metersReadings.sort((a, b) => 
      new Date(a.readingDate) - new Date(b.readingDate)
    );

    // Get the last two readings dates
    const lastReadingDate = new Date(sortedReadings[sortedReadings.length - 1].readingDate);
    const secondLastReadingDate = new Date(sortedReadings[sortedReadings.length - 2].readingDate);

    // Calculate period: 1 day after previous reading to last reading
    const startDate = new Date(secondLastReadingDate);
    startDate.setDate(startDate.getDate() + 1);

    const endDate = lastReadingDate;

    // Format dates
    const startFormatted = startDate.toLocaleDateString('fr-FR');
    const endFormatted = endDate.toLocaleDateString('fr-FR');
    const periodLabel = `Du ${startFormatted} au ${endFormatted}`;

    setBillingPeriodAuto(periodLabel);
    setPeriodDates({ startDate, endDate });
    form.setFieldValue('billingPeriod', periodLabel);
  };

  const generateInvoicesFormeters = async (values, subscriber, subscriberMeters, freshTariffs, freshTaxRate, paymentDueDays, freshReadings) => {
    let invoicesGenerated = 0;
    let alreadyInvoiced = 0;

    // Generate invoice for each meter
    subscriberMeters.forEach(meter => {
      // Get readings for meter (incluant les relevés initiaux pour les calculs)
      // Filtre sur meterid (minuscule car Supabase) ET cherche les données mappées correctement
      const meterReadings = freshReadings.filter(r => {
        // Vérifier les deux formats possibles (meterId et meterid)
        return r.meterId === meter.id || r.meterid === meter.id;
      });
      
      if (meterReadings.length < 2) {
        message.warning(`Pas assez de relevés pour le compteur ${meter.serialNumber}`);
        return;
      }

      // Sort readings by date
      const sortedReadings = meterReadings.sort((a, b) => 
        new Date(a.readingDate) - new Date(b.readingDate)
      );

      // Get last and previous readings
      const lastReadingObj = sortedReadings[sortedReadings.length - 1];
      const previousReadingObj = sortedReadings[sortedReadings.length - 2];
      
      // Check if last reading already has an invoice generated
      if (lastReadingObj.invoiceGenerated || lastReadingObj.linkedInvoiceId) {
        message.warning(`Le dernier relevé du compteur ${meter.serialNumber} a déjà généré une facture. Veuillez ajouter un nouveau relevé.`);
        alreadyInvoiced++;
        return;
      }

      const lastReading = lastReadingObj.meterReading;
      const previousReading = previousReadingObj.meterReading;
      const consumption = lastReading - previousReading;

      // Validate consumption
      if (consumption <= 0) {
        message.error(`Consommation invalide (${consumption} m³) pour le compteur ${meter.serialNumber}. Vérifiez les relevés.`);
        return;
      }

      // Calculate tariff using fresh tariffs from storage
      const applicableTariff = freshTariffs.find(t => 
        t.subscriberType === subscriber.type
      );

      if (!applicableTariff) {
        message.warning(`Aucun tarif trouvé pour le compteur ${meter.serialNumber} (type: ${subscriber.type}). Configurez un tarif pour ce type d'abonné.`);
        return;
      }

      const baseAmount = consumption * applicableTariff.pricePerM3;

      // Get tax rate from fresh settings
      const tax = baseAmount * (freshTaxRate / 100);
      const totalAmount = baseAmount + tax;

      // Create invoice
      const invoice = {
        invoiceNumber: `FAC-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        subscriberId: subscriber.id,
        subscriberName: `${subscriber.firstName} ${subscriber.lastName}`,
        meterId: meter.id,
        billingPeriod: values.billingPeriod,
        billingDate: new Date().toISOString(),
        dueDate: new Date(Date.now() + paymentDueDays * 24 * 60 * 60 * 1000).toISOString(),
        readingStart: previousReading,
        readingEnd: lastReading,
        totalConsumption: consumption,
        baseAmount: baseAmount,
        tax: tax,
        penalties: 0,
        totalAmount: totalAmount,
        paidAmount: 0,
        remainingAmount: totalAmount,
        status: 'issued',
        taxRate: freshTaxRate,
        tariffPrice: applicableTariff.pricePerM3
      };

      const newInvoice = addInvoice(invoice, user?.id);
      
      // Link the reading to the invoice to prevent double billing
      updateReadingInvoiceLink(lastReadingObj.id, newInvoice.id);
      invoicesGenerated++;
    });

    return { invoicesGenerated, alreadyInvoiced };
  };

  const handleGenerateInvoices = async (values) => {
    try {
      setLoading(true);
      const { subscriberId, billingPeriod } = values;
      
      // Check if billing period was calculated
      if (!billingPeriod || !periodDates.startDate || !periodDates.endDate) {
        setLoading(false);
        message.error('Veuillez sélectionner un abonné avec au moins 2 relevés de compteur');
        return;
      }

      // Check if generation date is >= last reading date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const lastReadingDate = new Date(periodDates.endDate);
      lastReadingDate.setHours(0, 0, 0, 0);

      if (today < lastReadingDate) {
        setLoading(false);
        message.error(`La facture ne peut pas être générée avant la date du dernier relevé (${lastReadingDate.toLocaleDateString('fr-FR')}). Date d'aujourd'hui: ${today.toLocaleDateString('fr-FR')}`);
        return;
      }

      // Get subscriber
      const subscriber = subscribers.find(s => s.id === subscriberId);
      if (!subscriber) {
        setLoading(false);
        message.error('Abonné non trouvé');
        return;
      }

      // Get subscriber meters
      const subscriberMeters = meters.filter(m => m.subscriberId === subscriberId);
      if (subscriberMeters.length === 0) {
        setLoading(false);
        message.error('Aucun compteur pour cet abonné');
        return;
      }

      // Get fresh tariffs and settings from storage (not from state)
      const freshTariffs = await storageService.getTariffs();
      const freshTaxRate = await storageService.getSetting('tax_rate', 5);
      const paymentDueDays = await storageService.getSetting('payment_due_days', 30);
      const freshReadings = await storageService.getReadings();

      setLoading(false);

      // Show confirmation modal
      Modal.confirm({
        title: 'Confirmation de génération de factures',
        content: (
          <div>
            <p><strong>Attention:</strong> Une fois générée, une facture ne peut pas être modifiée.</p>
            <p><strong>Détails de la génération:</strong></p>
            <ul>
              <li>Abonné: <strong>{subscriber.firstName} {subscriber.lastName}</strong></li>
              <li>Nombre de compteurs: <strong>{subscriberMeters.length}</strong></li>
              <li>Période: <strong>{billingPeriod}</strong></li>
              <li>Taux de taxe: <strong>{freshTaxRate}%</strong></li>
              <li>Délai de paiement: <strong>{paymentDueDays} jours</strong></li>
            </ul>
            <p>Êtes-vous sûr de vouloir générer la/les facture(s)?</p>
          </div>
        ),
        okText: 'Confirmer',
        cancelText: 'Annuler',
        onOk: async () => {
          try {
            setLoading(true);
            const result = await generateInvoicesFormeters(values, subscriber, subscriberMeters, freshTariffs, freshTaxRate, paymentDueDays, freshReadings);
            
            if (result.invoicesGenerated > 0) {
              message.success(`${result.invoicesGenerated} facture(s) générée(s) avec succès`);
            }
            if (result.alreadyInvoiced > 0) {
              message.info(`${result.alreadyInvoiced} compteur(s) n'a/n'ont pas pu être facturé(s) (facture(s) déjà générée(s))`);
            }
            
            setIsGenerateOpen(false);
            form.resetFields();
          } catch (error) {
            message.error('Erreur lors de la génération');
          } finally {
            setLoading(false);
          }
        },
        onCancel() {
          // Do nothing
        }
      });
    } catch (error) {
      message.error('Erreur lors de la vérification');
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Numéro',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber'
    },
    {
      title: 'Abonné',
      dataIndex: 'subscriberName',
      key: 'subscriberName'
    },
    {
      title: 'Période',
      dataIndex: 'billingPeriod',
      key: 'billingPeriod'
    },
    {
      title: 'Consommation (m³)',
      dataIndex: 'totalConsumption',
      key: 'totalConsumption',
      render: (consumption) => formatConsumption(consumption)
    },
    {
      title: 'Montant',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Payé',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Reste',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      render: (amount) => <span style={{ color: 'red' }}>{formatCurrency(amount)}</span>
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'green';
        if (status === 'issued' || status === 'partially_paid') color = 'orange';
        if (status === 'overdue') color = 'red';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Tooltip title="Voir les détails et imprimer">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedInvoice(record);
              setIsDetailOpen(true);
            }}
          >
            Détails
          </Button>
        </Tooltip>
      )
    }
  ];

  return (
    <div>
      <h1>Facturation et Relevés</h1>

      {isViewer && (
        <Alert
          message="Mode Consultation"
          description="Vous êtes en mode lecture seule. Vous ne pouvez pas générer de factures."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      {!isViewer && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsGenerateOpen(true)}
          style={{ marginBottom: 20 }}
        >
          Générer des factures
        </Button>
      )}

      <Table
        columns={columns}
        dataSource={invoices}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="Générer une facture"
        open={isGenerateOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsGenerateOpen(false)}
        width={600}
        okText="Générer"
        cancelText="Annuler"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerateInvoices}
        >
          <Form.Item
            label="Abonné"
            name="subscriberId"
            rules={[{ required: true, message: "L'abonné est obligatoire" }]}
          >
            <Select
              placeholder="Sélectionner un abonné"
              options={subscribers.map(s => ({
                label: `${s.firstName} ${s.lastName}`,
                value: s.id
              }))}
              onChange={handleSubscriberChange}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              showSearch
            />
          </Form.Item>

          <Form.Item
            label="Période de facturation"
            name="billingPeriod"
            rules={[{ required: true, message: 'La période est obligatoire' }]}
          >
            <Input 
              placeholder="Calculée automatiquement" 
              readOnly
              value={billingPeriodAuto}
            />
          </Form.Item>
        </Form>
      </Modal>

      <InvoiceDetail
        visible={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        subscriber={selectedInvoice ? subscribers.find(s => s.id === selectedInvoice.subscriberId) : null}
        meter={selectedInvoice ? meters.find(m => m.id === selectedInvoice.meterId) : null}
      />
    </div>
  );
};

export default BillingPage;
